import React, { useEffect, useState } from "react";
import { Plus, ClipboardCheck, Flag, History, X, ShieldAlert } from "lucide-react";
import { toast } from "../ui/Toast";

// Manager view for creating and running Performance Improvement Plans for
// their reports. Managers CANNOT approve (approve is admin-only, Session C) —
// no approve control here. The backend does NOT enforce lifecycle guards, so
// this view offers check-in / outcome only when status ∈ {active, extended}.

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const STATUS_STYLE = {
  draft:      "text-white/50 bg-white/5 border-white/15",
  active:     "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/25",
  extended:   "text-amber-400 bg-amber-500/10 border-amber-500/25",
  completed:  "text-blue-400 bg-blue-500/10 border-blue-500/20",
  terminated: "text-red-400 bg-red-500/10 border-red-500/20",
};
const STATUS_FALLBACK = "text-white/30 bg-white/5 border-white/10";
const STATUS_LABEL = { draft: "Draft", active: "Active", extended: "Extended", completed: "Completed", terminated: "Terminated" };

const RATING_OPTS  = [["on_track", "On Track"], ["needs_improvement", "Needs Improvement"], ["not_meeting", "Not Meeting"]];
const RATING_STYLE = {
  on_track:          "text-[#00ff90] bg-[#00ff90]/10 border-[#00ff90]/25",
  needs_improvement: "text-amber-400 bg-amber-500/10 border-amber-500/25",
  not_meeting:       "text-red-400 bg-red-500/10 border-red-500/20",
};
const RATING_LABEL = Object.fromEntries(RATING_OPTS);
const OUTCOME_OPTS = [["improved", "Improved (→ complete)"], ["extended", "Extended"], ["exited", "Exited (→ terminate)"]];
const FREQ_OPTS    = ["weekly", "biweekly", "monthly"];

const CAN_ACT = (s) => s === "active" || s === "extended";  // lifecycle guard (FE-enforced)
const fmtDate = (d) => (d ? String(d).slice(0, 10) : "—");
const badge = (map, key, label) => (
  <span className={`text-xs px-2 py-0.5 rounded-full border ${map[key] || STATUS_FALLBACK}`}>{label || key || "—"}</span>
);

const EMPTY_FORM = {
  employee_id: "", title: "", reason: "", goals: "",
  start_date: "", review_date: "", end_date: "", check_in_frequency: "weekly",
};

export default function ManagerPIPView() {
  const [pips, setPips]           = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);

  const [panel, setPanel]         = useState(null); // { id, kind: 'checkin'|'outcome'|'history' }
  const [checkForm, setCheckForm] = useState({ rating: "on_track", notes: "", employee_response: "" });
  const [outForm, setOutForm]     = useState({ outcome: "improved", notes: "" });
  const [history, setHistory]     = useState({}); // { [pipId]: [checkins] }

  const token = localStorage.getItem("token");
  const authH = { Authorization: `Bearer ${token}` };
  const jsonH = { ...authH, "Content-Type": "application/json" };

  const loadPips = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/performance/pip/team`, { headers: authH });
      const data = res.ok ? await res.json() : [];
      setPips(Array.isArray(data) ? data : []);
    } catch { setPips([]); }
    finally { setLoading(false); }
  };

  const loadEmployees = async () => {
    try {
      // Reports come from the goals/team endpoint (the PIP API has no employee
      // list). It returns {user_id, name, ...}.
      const res = await fetch(`${API_BASE}/api/performance/team`, { headers: authH });
      const data = res.ok ? await res.json() : [];
      const arr = Array.isArray(data) ? data : (data?.data || []);
      setEmployees(arr.map(r => ({ id: r.user_id, name: r.name || r.user_id })).filter(e => e.id));
    } catch { setEmployees([]); }
  };

  useEffect(() => { loadPips(); loadEmployees(); }, []); // eslint-disable-line

  const submitCreate = async () => {
    if (!form.employee_id || !form.title.trim() || !form.reason.trim() || !form.goals.trim()
        || !form.start_date || !form.review_date || !form.end_date) {
      toast.error("Fill in employee, title, reason, goals and all three dates");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/performance/pip`, {
        method: "POST", headers: jsonH, body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("PIP created — pending HR approval");
      setForm(EMPTY_FORM); setShowCreate(false);
      await loadPips();
    } catch (e) { toast.error("Failed to create PIP"); }
    finally { setSaving(false); }
  };

  const submitCheckin = async (id) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/performance/pip/${id}/checkin`, {
        method: "POST", headers: jsonH, body: JSON.stringify(checkForm),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("Check-in recorded");
      setCheckForm({ rating: "on_track", notes: "", employee_response: "" });
      setPanel(null);
      loadHistory(id, true);
    } catch { toast.error("Failed to record check-in"); }
    finally { setSaving(false); }
  };

  const submitOutcome = async (id) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/performance/pip/${id}/outcome`, {
        method: "PATCH", headers: jsonH, body: JSON.stringify(outForm),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success("Outcome recorded");
      setOutForm({ outcome: "improved", notes: "" });
      setPanel(null);
      await loadPips();
    } catch { toast.error("Failed to record outcome"); }
    finally { setSaving(false); }
  };

  const loadHistory = async (id, force = false) => {
    if (history[id] && !force) return;
    try {
      const res = await fetch(`${API_BASE}/api/performance/pip/${id}/checkins`, { headers: authH });
      const data = res.ok ? await res.json() : [];
      setHistory(h => ({ ...h, [id]: Array.isArray(data) ? data : [] }));
    } catch { setHistory(h => ({ ...h, [id]: [] })); }
  };

  const openPanel = (id, kind) => {
    if (panel && panel.id === id && panel.kind === kind) { setPanel(null); return; }
    setPanel({ id, kind });
    if (kind === "history") loadHistory(id);
  };

  return (
    <div className="space-y-5">
      {/* Header + New PIP */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShieldAlert size={16} className="text-amber-400" />
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">PIP Management</span>
        </div>
        <button onClick={() => setShowCreate(s => !s)}
          className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-[#00ff90]/25 bg-[#00ff90]/8 text-[#00ff90]/70 hover:bg-[#00ff90]/15 hover:text-[#00ff90] transition-colors">
          <Plus size={12} /> New PIP
        </button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className={`${card} p-5 space-y-4`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Employee</label>
              <select className={inputCls} value={form.employee_id}
                onChange={e => setForm(f => ({ ...f, employee_id: e.target.value }))}>
                <option value="">Select a report…</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Check-in Frequency</label>
              <select className={inputCls} value={form.check_in_frequency}
                onChange={e => setForm(f => ({ ...f, check_in_frequency: e.target.value }))}>
                {FREQ_OPTS.map(o => <option key={o} value={o}>{o[0].toUpperCase() + o.slice(1)}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Title</label>
              <input className={inputCls} value={form.title} placeholder="e.g. Improve delivery consistency"
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Reason</label>
              <textarea className={inputCls} rows={2} value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Goals</label>
              <textarea className={inputCls} rows={2} value={form.goals}
                onChange={e => setForm(f => ({ ...f, goals: e.target.value }))} />
            </div>
            {[["start_date", "Start Date"], ["review_date", "Review Date"], ["end_date", "End Date"]].map(([k, l]) => (
              <div key={k}>
                <label className={labelCls}>{l}</label>
                <input type="date" className={inputCls} value={form[k]}
                  onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} />
              </div>
            ))}
          </div>
          <div className="flex gap-2 justify-end pt-1 border-t border-white/5">
            <button onClick={() => { setShowCreate(false); setForm(EMPTY_FORM); }}
              className="text-xs px-4 py-2 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
            <button onClick={submitCreate} disabled={saving}
              className="text-xs px-5 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
              {saving ? "Saving…" : "Create PIP"}
            </button>
          </div>
        </div>
      )}

      {/* PIP list */}
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">Team PIPs</span>
        </div>
        <div className="divide-y divide-white/5">
          {loading ? (
            Array.from({ length: 2 }).map((_, i) => <div key={i} className="px-5 py-4"><div className="h-12 rounded-lg bg-white/5 animate-pulse" /></div>)
          ) : pips.length === 0 ? (
            <p className="px-5 py-8 text-white/20 text-sm italic text-center">No PIPs for your team.</p>
          ) : pips.map(p => (
            <div key={p.id}>
              <div className="flex items-start justify-between gap-3 px-5 py-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <p className="text-sm text-white/80 font-medium">{p.employee_name || p.employee_id}</p>
                    {badge(STATUS_STYLE, p.status, STATUS_LABEL[p.status])}
                  </div>
                  <p className="text-xs text-white/40">{p.title}</p>
                  <p className="text-xs text-white/25 mt-0.5">
                    {fmtDate(p.start_date)} → {fmtDate(p.review_date)} → {fmtDate(p.end_date)} · {p.check_in_frequency}
                  </p>
                  {p.outcome && (
                    <p className="text-xs text-white/40 mt-0.5">Outcome: <span className="text-white/60">{p.outcome}</span>{p.outcome_notes ? ` — ${p.outcome_notes}` : ""}</p>
                  )}
                </div>
                <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
                  {p.status === "draft" && (
                    <span className="text-xs px-3 py-1.5 rounded-lg border border-amber-500/20 bg-amber-500/5 text-amber-400/70">Pending HR approval</span>
                  )}
                  {CAN_ACT(p.status) && (
                    <>
                      <button onClick={() => openPanel(p.id, "checkin")}
                        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/50 hover:text-[#00ff90] transition-colors">
                        <ClipboardCheck size={12} /> Check-in
                      </button>
                      <button onClick={() => openPanel(p.id, "outcome")}
                        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/50 hover:text-amber-400 transition-colors">
                        <Flag size={12} /> Outcome
                      </button>
                    </>
                  )}
                  <button onClick={() => openPanel(p.id, "history")}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">
                    <History size={12} /> History
                  </button>
                </div>
              </div>

              {/* Inline panels */}
              {panel && panel.id === p.id && (
                <div className="mx-5 mb-4 rounded-xl border border-[#00ff90]/15 bg-[#00ff90]/3 p-4">
                  {panel.kind === "checkin" && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className={labelCls}>Rating</label>
                          <select className={inputCls} value={checkForm.rating}
                            onChange={e => setCheckForm(f => ({ ...f, rating: e.target.value }))}>
                            {RATING_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className={labelCls}>Manager Notes</label>
                          <input className={inputCls} value={checkForm.notes}
                            onChange={e => setCheckForm(f => ({ ...f, notes: e.target.value }))} />
                        </div>
                        <div className="md:col-span-3">
                          <label className={labelCls}>Employee Response (optional)</label>
                          <input className={inputCls} value={checkForm.employee_response}
                            onChange={e => setCheckForm(f => ({ ...f, employee_response: e.target.value }))} />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setPanel(null)} className="text-xs px-4 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
                        <button onClick={() => submitCheckin(p.id)} disabled={saving}
                          className="text-xs px-4 py-1.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors">Record Check-in</button>
                      </div>
                    </div>
                  )}

                  {panel.kind === "outcome" && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className={labelCls}>Outcome</label>
                          <select className={inputCls} value={outForm.outcome}
                            onChange={e => setOutForm(f => ({ ...f, outcome: e.target.value }))}>
                            {OUTCOME_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className={labelCls}>Notes</label>
                          <input className={inputCls} value={outForm.notes}
                            onChange={e => setOutForm(f => ({ ...f, notes: e.target.value }))} />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button onClick={() => setPanel(null)} className="text-xs px-4 py-1.5 rounded-lg border border-white/10 text-white/40 hover:text-white transition-colors">Cancel</button>
                        <button onClick={() => submitOutcome(p.id)} disabled={saving}
                          className="text-xs px-4 py-1.5 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 disabled:opacity-40 transition-colors">Record Outcome</button>
                      </div>
                    </div>
                  )}

                  {panel.kind === "history" && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-[#00ff90]/60 uppercase tracking-wider">Check-in History</p>
                        <button onClick={() => setPanel(null)} className="text-white/30 hover:text-white transition-colors"><X size={12} /></button>
                      </div>
                      {!history[p.id] ? (
                        <p className="text-xs text-white/25 italic">Loading…</p>
                      ) : history[p.id].length === 0 ? (
                        <p className="text-xs text-white/25 italic">No check-ins recorded yet.</p>
                      ) : history[p.id].map(c => (
                        <div key={c.id} className="rounded-lg border border-white/5 bg-white/3 p-3 space-y-1">
                          <div className="flex items-center justify-between gap-2">
                            {badge(RATING_STYLE, c.rating, RATING_LABEL[c.rating])}
                            <span className="text-xs text-white/25">{fmtDate(c.completed_at || c.created_at)}</span>
                          </div>
                          {c.manager_notes && <p className="text-xs text-white/60"><span className="text-white/30">Manager: </span>{c.manager_notes}</p>}
                          {c.employee_response && <p className="text-xs text-white/60"><span className="text-white/30">Employee: </span>{c.employee_response}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
